'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { ArrowLeft, ChevronDown, X } from 'lucide-react'

export type BreakdownLine = { label: string; amount: number; accent?: boolean }

type Props = {
  /** 1-based, sobre los pasos que el cliente realmente ve (sin saltados ni confirmación). */
  stepIndex: number
  stepCount: number
  title: string
  total: number
  /** Domicilio todavía sin cotizar: el total no incluye el envío y lo dice. */
  pendingDeliveryFee: boolean
  /** Desglose que se abre al tocar el total. Mismas cifras que calcula OrderSheet. */
  breakdown: BreakdownLine[]
  accentColor: string
  onBack?: () => void
  onClose: () => void
  /**
   * Pasos que ya traen su propio encabezado (Dirección, Datos, Propina, Donación,
   * Pago — Pago no se toca): solo una fila con "Paso X de Y" y el total, sin
   * título, Atrás ni cerrar, para no duplicarlos.
   */
  slim?: boolean
  /** Sin total (Donación y Pago: el cobro suma la donación y Pago trae su resumen). */
  hideTotal?: boolean
}

/**
 * Encabezado del checkout por pasos (spec 2026-10-19 §5.4 y §5.7): "Paso 2 de 5",
 * el título del paso, una barra de progreso fina y el total a la derecha, en
 * TODOS los pasos (Pago incluido no: tiene su propio resumen). Va arriba y no en
 * el pie porque Dirección, Datos, Propina y Pago tienen su propio botón y Pago no
 * se toca (plan PR 3, decisión 1).
 */
export function CheckoutHeaderV2({
  stepIndex,
  stepCount,
  title,
  total,
  pendingDeliveryFee,
  breakdown,
  accentColor,
  onBack,
  onClose,
  slim = false,
  hideTotal = false,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tCommon = useTranslations('common')
  const [open, setOpen] = useState(false)
  const money = `$${total.toFixed(2)}`
  const pct = stepCount > 0 ? Math.min(100, Math.round((stepIndex / stepCount) * 100)) : 0

  return (
    <div className="flex-shrink-0 border-b border-gray-100" data-testid="checkout-header-v2">
      <div className={`flex items-center gap-2 ${slim ? 'px-4 pb-1.5' : 'px-3 pt-1 pb-2'}`}>
        {slim ? null : onBack ? (
          <button
            type="button"
            onClick={onBack}
            aria-label={t('back')}
            className="w-9 h-9 flex-shrink-0 rounded-full hover:bg-gray-100 flex items-center justify-center"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
        ) : (
          <span className="w-2 flex-shrink-0" aria-hidden />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-gray-500">
            {t('stepCounter', { current: stepIndex, total: stepCount })}
          </p>
          {!slim && <h2 className="text-lg font-bold text-gray-900 leading-tight line-clamp-2">{title}</h2>}
        </div>
        {!hideTotal && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-label={t('totalBreakdownShow')}
          className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-gray-50 px-3 py-1.5 text-sm font-bold text-gray-900"
        >
          {pendingDeliveryFee ? t('totalPlusDelivery', { total: money }) : t('totalLabel', { total: money })}
          <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        )}
        {!slim && (
        <button
          type="button"
          onClick={onClose}
          aria-label={tCommon('close')}
          className="w-9 h-9 flex-shrink-0 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>
        )}
      </div>
      {open && !hideTotal && (
        <div className="px-4 pb-2 space-y-1 text-sm" data-testid="checkout-breakdown">
          {breakdown.map((l) => (
            <div
              key={l.label}
              className={`flex justify-between ${l.accent ? 'font-semibold' : 'text-gray-600'}`}
              style={l.accent ? { color: accentColor } : undefined}
            >
              <span>{l.label}</span>
              <span>{l.amount < 0 ? `−$${Math.abs(l.amount).toFixed(2)}` : `$${l.amount.toFixed(2)}`}</span>
            </div>
          ))}
        </div>
      )}
      <div className="h-[3px] bg-gray-100" aria-hidden>
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: accentColor }} />
      </div>
    </div>
  )
}
